import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import type { Comissao, FaturamentoPeriodo, CupomMaisUsado, Cliente, Pedido, PedidoComItens, StatusPedido, CentralDeComando } from '@pgcustom/shared-types';
import type { PgTable, PgTableColumn, PgTableBadgeCell, PgChart, PgChartPoint, PgKpiCard } from '@pgcustom/design-system';
import type { Resultado } from '../../core/resultado.js';
import { logger } from '../../core/logger.js';
import type { RelatoriosApi } from '../relatorios/relatorios.api.js';
import { CSS_SITE_TRAFEGO, HTML_CARD_SITE_TRAFEGO, ligarCardSiteTrafego } from '../relatorios/site-trafego.card.js';
import { HTML_CARD_VENDAS_ORIGEM, ligarCardVendasOrigem } from '../relatorios/vendas-origem.card.js';
import type { SocietarioApi } from '../societario/societario.api.js';
import type { ComissoesApi } from '../comissoes/comissoes.api.js';
import type { FinanceiroApi } from '../financeiro/financeiro.api.js';
import type { ParceirosApi } from '../parceiros/parceiros.api.js';
import type { ClientesApi } from '../clientes/clientes.api.js';
import type { DashboardApi, ItemAviso } from '../dashboard/dashboard.api.js';
import type { UsuariosApi } from '../usuarios/usuarios.api.js';
import type { PedidosApi } from '../pedidos/pedidos.api.js';
import { rotuloStatusPedido } from '../pedidos/status-pedido.js';
import { formatarDataHora } from '../pedidos/formatacao.js';

const STATUS_BONUS_BADGE: Record<string, PgTableBadgeCell> = {
  proposto: { texto: 'Proposto', tom: 'neutro' },
  aprovado: { texto: 'Aprovado', tom: 'destaque' },
  pago: { texto: 'Pago', tom: 'sucesso' },
  rejeitado: { texto: 'Rejeitado', tom: 'erro' },
  cancelado: { texto: 'Cancelado', tom: 'erro' },
};

/**
 * "Pedidos recentes" — `comercial.pedidos.status` só tem 3 valores reais
 * (`pedido.ts`, mesma limitação documentada em `integracao-legado`: o
 * legado tem ~12 estados operacionais + pagamento/envio separados, mas
 * o schema atual do ERP reduz tudo a isso). "Status" abaixo é só uma
 * descrição mais longa do MESMO valor real (nunca uma 2ª dimensão
 * inventada); "Pagamento" é o badge compacto com o rótulo curto padrão
 * (`rotuloStatusPedido`) — duas apresentações visuais do mesmo dado.
 */
const DESCRICAO_STATUS_PEDIDO: Record<StatusPedido, string> = {
  pendente: 'Aguardando pagamento',
  pago: 'Pagamento aprovado',
  cancelado: 'Cancelado',
};
const TOM_STATUS_PEDIDO: Record<StatusPedido, 'sucesso' | 'alerta' | 'erro'> = {
  pendente: 'alerta',
  pago: 'sucesso',
  cancelado: 'erro',
};

/**
 * "Atividades recentes" — rótulos amigáveis para os nomes reais de
 * evento do barramento (`packages/shared-types/src/events/catalog.ts`),
 * a mesma fonte que alimenta `RegistroAuditoria.acao`. Cobre os eventos
 * operacionais mais relevantes para o CEO (pedidos, produção, cupons,
 * parceiros, garantias, comissões/bônus); qualquer evento fora deste
 * mapa cai no fallback (nome do evento com pontuação trocada por
 * espaço) — nunca escondido, nunca um rótulo inventado sem
 * correspondência real. Não existe evento de login/redefinição de senha
 * no catálogo hoje (o barramento de eventos é só de domínio de negócio),
 * então esses dois tipos de atividade não aparecem aqui — limitação
 * real do sistema, não uma omissão de implementação.
 */
const ROTULOS_ACAO_AUDITORIA: Record<string, string> = {
  'pedido.criado': 'criou um pedido',
  'pedido.pago': 'recebeu pagamento de um pedido',
  'pedido.cancelado': 'cancelou um pedido',
  'cliente.criado': 'cadastrou um cliente',
  'cliente.atualizado': 'atualizou um cliente',
  'cliente.excluido': 'excluiu um cliente',
  'cupom.criado': 'criou um cupom',
  'parceiro.criado': 'criou um parceiro',
  'parceiro.atualizado': 'atualizou um parceiro',
  'ops.criada': 'criou uma OPS',
  'ops.atualizada': 'atualizou uma OPS',
  'ops.concluida': 'concluiu uma OPS',
  'garantia.aberta': 'abriu uma garantia',
  'garantia.acionada': 'acionou uma garantia',
  'ticket.criado': 'abriu um ticket de suporte',
  'ticket.fechado': 'fechou um ticket de suporte',
  'envio.criado': 'criou um envio',
  'envio.despachado': 'despachou um envio',
  'envio.entregue': 'marcou um envio como entregue',
  'comissao.gerada': 'gerou uma comissão',
  'comissao.paga': 'pagou uma comissão',
  'bonus_pg.registrado': 'registrou um bônus PG',
  'bonus_pg.aprovado': 'aprovou um bônus PG',
  'bonus_pg.pago': 'pagou um bônus PG',
  'cliente.portal_autenticado': 'fez login no Portal do Cliente',
  'parceiro.portal_autenticado': 'fez login no Portal do Parceiro',
  'cms.publicado': 'publicou conteúdo no site',
  'insumo.criado': 'cadastrou um insumo',
  'insumo.atualizado': 'atualizou um insumo',
};

/** Mesmas opções de `dashboard.page.ts` — padrão já estabelecido, reaproveitado aqui. */
const OPCOES_PERIODO = [3, 6, 12] as const;

/**
 * "Taxa de conversão" — `estado="indisponivel"` fixo, de propósito, nunca calculado. Fórmula
 * futura: conversões (pedidos pagos — MESMA definição de `status = 'pago'` já usada em
 * `pedidosPagosMes`/`PulsoNegocio`, nunca uma 2ª regra) / sessões-ou-leads × 100.
 *
 * Não existe hoje, neste repositório, nenhuma fonte real de sessões/tráfego/leads — nenhuma
 * tabela, evento ou integração de analytics (sem Meta Pixel/GA4/gtag/fbq/UTM em lugar nenhum do
 * ERP). O site público (repo `pgcustom-art`, fora deste projeto) tem Meta Pixel instalado, mas
 * esse dado vive só no Gerenciador de Eventos da Meta — nunca foi sincronizado para o banco do
 * ERP, então não é uma fonte disponível AQUI. Só aplicar a fórmula quando essa integração existir
 * de verdade (fora do escopo desta correção) — até lá, o valor central continua "—" e a `dica`
 * só explica objetivamente por quê, nunca inventa um número.
 */
const HTML_DONUT_CONVERSAO = `<pg-donut-kpi id="donut-conversao" titulo="Taxa de conversão" cor="azul" estado="indisponivel" dica="sem dados de tráfego/leads integrados"></pg-donut-kpi>`;

/**
 * Meta operacional definida pela administração (não é dado de banco —
 * não existe módulo de Metas nesta sprint, só este número fixo,
 * combinado diretamente com o owner). Progresso é sempre derivado do
 * MESMO `pedidosPagosMes` já usado pelo KPI "Pedidos pagos (mês)" e
 * pelo donut "Pedidos pagos (mês)" (`carregarFinanceiro`,
 * `PulsoNegocio.pedidosPagosMes` — `status = 'pago'` no mês corrente),
 * nunca uma segunda consulta ou definição divergente.
 */
const META_PEDIDOS_PAGOS_MES = 14;

/**
 * Despesa fixa indispensável da operação — sem denominador, sem parcela = recorrente
 * indefinida (ex: aluguel); com `parcela`, é um financiamento com fim definido, e `numero` é
 * SEMPRE a próxima parcela ainda pendente (nunca a última já paga).
 */
interface DespesaFixa {
  nome: string;
  valorCentavos: number;
  /** Dia do mês (1-31) em que a despesa vence — mesmo dia todo mês/ciclo. */
  diaVencimento: number;
  parcela?: { numero: number; total: number };
}

/**
 * Despesas fixas indispensáveis da operação, definidas diretamente pelo owner nesta sessão —
 * não existe domínio de despesas futuras no sistema hoje (`financeiro.transacoes` só registra
 * transações JÁ realizadas, nunca um calendário de vencimentos — mesma limitação documentada
 * antes neste card). Estrutura local, isolada e claramente substituível por dados persistidos
 * quando esse domínio existir; nenhum valor aqui foi inventado, só o que foi informado.
 *
 * "PC Igor Roberto": 2 das 12 parcelas já pagas (informado pelo owner) — `numero: 3` é a
 * PRÓXIMA parcela pendente, não a última paga. Como não há histórico de pagamento persistido,
 * esse número é mantido manualmente aqui até existir uma fonte real (igual ao valor da meta de
 * pedidos, `META_PEDIDOS_PAGOS_MES` acima) — a data de vencimento em si, porém, é sempre
 * recalculada a partir do relógio real (`proximoVencimento`), nunca fixa.
 */
const DESPESAS_FIXAS: DespesaFixa[] = [
  { nome: 'Aluguel Relvado', valorCentavos: 150_000, diaVencimento: 15 },
  { nome: 'PC Igor Roberto', valorCentavos: 50_000, diaVencimento: 20, parcela: { numero: 3, total: 12 } },
];

/**
 * `<erp-dashboard-ceo-page>` — visão do Diretor/CEO (referência visual
 * "fase de finalização", perfil Latway). Vive dentro do `<erp-shell>`
 * existente (mesmo sidebar/header do ERP interno) — só o conteúdo do
 * outlet é específico deste perfil.
 *
 * "Seu Lucro" é SEMPRE `lucroDoMes × participação ATIVA REAL da própria
 * pessoa` — nunca um percentual hardcoded (o mockup mostrava "56%" como
 * dado de exemplo, não uma regra). Sem participação societária
 * registrada, mostra um estado explícito, nunca inventa um percentual.
 * "Sua participação societária"/"Seus bônus PG" (presentes na referência original) foram
 * substituídos por "Metas" (barra de progresso da meta de pedidos) e "Próximos pagamentos"
 * (lista de despesas fixas) — os dados de participação/bônus continuam alimentando os
 * KPIs/donuts normalmente (`carregarSocietario()`, inalterado). A coluna lateral do gráfico
 * também tem "Alertas" (resumo textual) e "Próximo vencimento" (destaque de 1 item),
 * reaproveitando a MESMA meta de pedidos e a MESMA lista de despesas fixas
 * (`META_PEDIDOS_PAGOS_MES`/`DESPESAS_FIXAS` abaixo) — nenhuma lógica duplicada entre os
 * quatro cards.
 */
export class ErpDashboardCeoPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _relatoriosApi: RelatoriosApi | null = null;
  private _societarioApi: SocietarioApi | null = null;
  private _comissoesApi: ComissoesApi | null = null;
  private _financeiroApi: FinanceiroApi | null = null;
  private _parceirosApi: ParceirosApi | null = null;
  private _clientesApi: ClientesApi | null = null;
  private _dashboardApi: DashboardApi | null = null;
  private _usuariosApi: UsuariosApi | null = null;
  private _pedidosApi: PedidosApi | null = null;
  private _nomeUsuario = '';
  private periodoMeses: (typeof OPCOES_PERIODO)[number] = 6;
  /** Lista completa (até 100, mais recentes primeiro) já buscada por `carregarPedidosOperacional` — reaproveitada para o KPI "Pedidos novos (hoje)", "Pedidos recentes" e a contagem por cliente de "Últimos clientes" (nenhuma chamada nova por seção). */
  private pedidosRecentesTodos: Pedido[] | null = null;
  private pedidosOperacionalIndisponivel = false;
  private clientesRecentesLista: Cliente[] | null = null;
  private avisosResultado: Resultado<ItemAviso[]> = { status: 'carregando' };
  private centralDeComandoResultado: Resultado<CentralDeComando> = { status: 'carregando' };

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set relatoriosApi(valor: RelatoriosApi) {
    this._relatoriosApi = valor;
  }

  set societarioApi(valor: SocietarioApi) {
    this._societarioApi = valor;
  }

  set comissoesApi(valor: ComissoesApi) {
    this._comissoesApi = valor;
  }

  set financeiroApi(valor: FinanceiroApi) {
    this._financeiroApi = valor;
  }

  set parceirosApi(valor: ParceirosApi) {
    this._parceirosApi = valor;
  }

  set clientesApi(valor: ClientesApi) {
    this._clientesApi = valor;
  }

  set dashboardApi(valor: DashboardApi) {
    this._dashboardApi = valor;
  }

  set usuariosApi(valor: UsuariosApi) {
    this._usuariosApi = valor;
  }

  set pedidosApi(valor: PedidosApi) {
    this._pedidosApi = valor;
  }

  set nomeUsuario(valor: string) {
    this._nomeUsuario = valor;
  }

  connectedCallback(): void {
    this.render();
    void this.carregar();
    // Card "Site e tráfego" (GA4 + pedidos pagos do ERP): implementação única em
    // relatorios/site-trafego.card.ts, também usada pelo Dashboard operacional.
    ligarCardSiteTrafego(this.root, this._relatoriosApi);
    ligarCardVendasOrigem(this.root, this._relatoriosApi);
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        h1 {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-xl, 1.5rem);
          letter-spacing: -0.01em;
          color: var(--pg-color-texto-primario, #f2f3f5);
          margin: 0 0 var(--pg-space-1, 4px);
        }
        h1 span { color: var(--pg-color-roxo, #8b5cf6); }
        .cabecalho { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--pg-space-4, 16px); flex-wrap: wrap; margin-bottom: var(--pg-space-6, 32px); }
        .sub { color: var(--pg-color-texto-secundario, #9aa1ab); font-size: var(--pg-font-size-sm, 0.875rem); margin: var(--pg-space-1, 4px) 0 0; }
        .seletor-periodo {
          background: var(--pg-color-bg-elevado, #1a1d21);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-md, 10px);
          color: var(--pg-color-texto-primario, #f2f3f5);
          font-family: var(--pg-font-familia-base, system-ui);
          font-size: var(--pg-font-size-sm, 0.875rem);
          padding: var(--pg-space-2, 8px) var(--pg-space-3, 12px);
          cursor: pointer;
          transition: border-color 0.15s ease;
        }
        .seletor-periodo:hover { border-color: var(--pg-color-texto-desabilitado, #5c626b); }
        /* Fileira de KPIs. Passou de 8 para 9 cards (auditoria do Dashboard: "Faturamento anual").
           Nove cards de largura mínima confortável (~190px, o mesmo piso do Dashboard geral)
           não cabem numa linha só fora de telas ultrawide — então volta ao auto-fit por pixel,
           que reflui em 2 linhas de forma legível em qualquer largura (nunca corta o número do
           card, que era o risco real do minmax menor anterior). */
        .kpis {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
          gap: var(--pg-space-4, 16px);
          margin-bottom: var(--pg-space-6, 32px);
        }
        @media (max-width: 700px) {
          .kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        /* CAUSA RAIZ (histórico) do vão vazio abaixo de "Evolução financeira": não era só o
           pg-card esticando por causa do seu ":host { height: 100% }" — era a COLUNA LATERAL ter
           o formato errado (pilha 1×4, altura bem maior que a do gráfico sozinho). GRADE 2×2 (2
           colunas × 2 linhas), não uma pilha 1×4 — nesse formato, a altura natural dos 4 cards
           somados (Clientes & Cupons, Cupons mais usados, Alertas, Próximo vencimento) fica muito
           mais próxima da altura natural do gráfico (chart + legenda + eixo X), então esticar as
           duas colunas pra ficarem iguais (align-items: stretch, o padrão do grid) não cria vão
           vazio perceptível. */
        .area-principal {
          display: grid;
          grid-template-columns: minmax(0, 2.05fr) minmax(330px, 1fr);
          gap: var(--pg-space-4, 16px);
          margin-bottom: var(--pg-space-6, 32px);
        }
        .area-principal-lateral {
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: 1fr 1fr;
          gap: var(--pg-space-4, 16px);
          min-width: 0;
        }
        @media (max-width: 1100px) {
          .area-principal { grid-template-columns: 1fr; }
        }
        /* Linha de destaque entre o gráfico e a fileira de donuts — hoje mostra "Metas" (barra de
           progresso da meta de pedidos) e "Próximos pagamentos" (lista das despesas fixas), no
           lugar de "Sua participação societária"/"Seus bônus PG" (esses dados continuam
           alimentando os KPIs/donuts correspondentes — carregarSocietario() não muda, só parou de
           ter um card próprio aqui). Grid simples (stretch, o padrão), sem override de altura no
           pg-card — os dois cards têm conteúdo de tamanho parecido (uma barra de progresso vs.
           uma lista de 2 itens), então esticar pra igualar não cria vão vazio perceptível. */
        .secoes-societario {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: var(--pg-space-4, 16px);
          margin-bottom: var(--pg-space-6, 32px);
        }
        /* Card "Metas" (linha de destaque acima) — barra de progresso da meta fixa de pedidos
           pagos/mês (META_PEDIDOS_PAGOS_MES), sobre o mesmo pedidosPagosMes do KPI/donut
           "Pedidos pagos (mês)". */
        .meta-card { display: flex; flex-direction: column; gap: var(--pg-space-2, 8px); }
        .meta-periodo { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); }
        .meta-linha { display: flex; align-items: baseline; justify-content: space-between; gap: var(--pg-space-2, 8px); }
        .meta-rotulo { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); }
        .meta-percentual { font-size: var(--pg-font-size-sm, 0.875rem); font-weight: var(--pg-font-weight-semibold, 600); color: var(--pg-color-dourado, #c9a24b); font-variant-numeric: tabular-nums; }
        .meta-valor { font-size: var(--pg-font-size-sm, 0.875rem); font-weight: var(--pg-font-weight-semibold, 600); color: var(--pg-color-texto-primario, #f2f3f5); font-variant-numeric: tabular-nums; }
        .meta-barra { height: 6px; border-radius: var(--pg-radius-full, 9999px); background: var(--pg-color-bg-base, #121417); overflow: hidden; }
        .meta-barra-preenchimento {
          height: 100%;
          border-radius: inherit;
          background: linear-gradient(90deg, color-mix(in srgb, var(--pg-color-dourado, #c9a24b) 65%, white), var(--pg-color-dourado, #c9a24b));
        }
        .meta-status { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); }
        .meta-status.atingida { color: var(--pg-color-sucesso, #3bb273); font-weight: var(--pg-font-weight-medio, 500); }
        /* Card "Próximos pagamentos" (linha de destaque acima) — lista compacta das despesas
           fixas (DESPESAS_FIXAS), mesmo padrão visual de .alerta-item/.atividade-item (separador
           sutil entre itens, sem borda no último). */
        .pagamentos-lista { display: flex; flex-direction: column; gap: var(--pg-space-3, 12px); }
        .pagamento-item { display: flex; flex-direction: column; gap: 2px; padding-bottom: var(--pg-space-3, 12px); border-bottom: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36); }
        .pagamento-item:last-child { border-bottom: none; padding-bottom: 0; }
        .pagamento-linha { display: flex; align-items: baseline; justify-content: space-between; gap: var(--pg-space-2, 8px); }
        .pagamento-nome { font-size: var(--pg-font-size-sm, 0.875rem); font-weight: var(--pg-font-weight-medio, 500); color: var(--pg-color-texto-primario, #f2f3f5); }
        .pagamento-valor { font-size: var(--pg-font-size-sm, 0.875rem); font-weight: var(--pg-font-weight-semibold, 600); color: var(--pg-color-dourado, #c9a24b); font-variant-numeric: tabular-nums; white-space: nowrap; }
        .pagamento-meta { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); }
        /* Card "Alertas" (coluna lateral do gráfico) — resumo textual combinando a MESMA fonte de
           "Metas" (meta de pedidos ainda não atingida) e "Próximos pagamentos"/"Próximo
           vencimento" (despesa fixa mais próxima de vencer) — nenhuma consulta nova, nenhum
           número inventado. Lista curta (hoje, no máximo 2 itens), mesmo padrão visual compacto
           do resto da coluna lateral. */
        .alertas-resumo { display: flex; flex-direction: column; gap: var(--pg-space-3, 12px); }
        .alertas-resumo-contagem { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); }
        .alertas-resumo-lista { display: flex; flex-direction: column; gap: var(--pg-space-2, 8px); margin: 0; padding: 0; list-style: none; }
        .alertas-resumo-lista li { display: flex; align-items: baseline; gap: 8px; font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-primario, #f2f3f5); }
        .alertas-resumo-lista li::before { content: '•'; color: var(--pg-color-dourado, #c9a24b); flex-shrink: 0; }
        /* Card "Próximo vencimento" (coluna lateral do gráfico) — só a despesa fixa de vencimento
           mais próximo, em destaque (selo circular dia+mês, dourado — identidade PG). MESMA fonte
           de "Próximos pagamentos" (proximasDespesasOrdenadas) — só muda a apresentação (1 item
           em destaque vs. lista completa), nenhuma lógica duplicada. */
        .vencimento-card { display: flex; align-items: center; gap: var(--pg-space-3, 12px); }
        .vencimento-selo {
          flex-shrink: 0;
          width: 52px;
          height: 52px;
          border-radius: var(--pg-radius-lg, 14px);
          background: color-mix(in srgb, var(--pg-color-dourado, #c9a24b) 14%, var(--pg-color-bg-base, #121417));
          border: 1px solid color-mix(in srgb, var(--pg-color-dourado, #c9a24b) 35%, transparent);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          line-height: 1.1;
        }
        .vencimento-selo .dia { font-size: var(--pg-font-size-lg, 1.125rem); font-weight: var(--pg-font-weight-bold, 700); color: var(--pg-color-dourado, #c9a24b); font-variant-numeric: tabular-nums; }
        .vencimento-selo .mes { font-size: 9px; font-weight: var(--pg-font-weight-semibold, 600); color: var(--pg-color-dourado, #c9a24b); letter-spacing: 0.04em; }
        .vencimento-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .vencimento-nome { font-size: var(--pg-font-size-sm, 0.875rem); font-weight: var(--pg-font-weight-medio, 500); color: var(--pg-color-texto-primario, #f2f3f5); }
        .vencimento-parcela { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); }
        .vencimento-valor { font-size: var(--pg-font-size-sm, 0.875rem); font-weight: var(--pg-font-weight-semibold, 600); color: var(--pg-color-dourado, #c9a24b); font-variant-numeric: tabular-nums; }
        .vencimento-meta { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); }
        /* Área operacional: 2 colunas de largura igual (Atividades recentes | Últimos clientes) —
           o card "Alertas" que ocupava a 1ª das 3 colunas foi removido só da apresentação deste
           Dashboard (a lógica/dados de avisos continuam existindo, ver carregarAvisos()/
           carregarCentralDeComando()/renderAlertas(), inalterados — só perderam o card que os
           mostrava aqui). "Pedidos recentes" continua fora desta grade, como card de largura
           total sozinho logo abaixo (".pedidos-recentes-full"). Sem override de altura no pg-card
           (stretch, o padrão do grid) — atividades (lista com scroll próprio até 320px) e
           clientes (tabela até 5 linhas) têm alturas naturais parecidas, então esticar pra
           igualar os dois não cria vão vazio perceptível. */
        .secoes {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: var(--pg-space-4, 16px);
        }
        @media (max-width: 1100px) {
          .secoes { grid-template-columns: 1fr; }
        }
        .pedidos-recentes-full { margin-top: var(--pg-space-4, 16px); }
        /* 6 indicadores em donut — mesma lógica de colunas fixas dos KPIs (não auto-fit): garante
           os 6 numa única linha em desktop, sem depender de largura exata de viewport pra decidir
           quantos cabem por linha. */
        .donuts-kpi {
          display: grid;
          grid-template-columns: repeat(6, minmax(0, 1fr));
          gap: var(--pg-space-4, 16px);
          margin-top: var(--pg-space-6, 32px);
        }
        @media (max-width: 1100px) {
          .donuts-kpi { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @media (max-width: 600px) {
          .donuts-kpi { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        .secoes.operacional { margin-top: var(--pg-space-6, 32px); }
        .link-rodape {
          font-size: var(--pg-font-size-xs, 0.75rem);
          font-weight: var(--pg-font-weight-medio, 500);
          color: var(--pg-color-dourado, #c9a24b);
          text-decoration: none;
          white-space: nowrap;
        }
        .link-rodape:hover { text-decoration: underline; }
        .alertas-lista { display: flex; flex-direction: column; gap: var(--pg-space-3, 12px); }
        .alerta-item { display: flex; align-items: center; justify-content: space-between; gap: var(--pg-space-3, 12px); padding: var(--pg-space-2, 8px) 0; border-bottom: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36); }
        .alerta-item:last-child { border-bottom: none; }
        .alerta-titulo { display: flex; align-items: center; gap: var(--pg-space-2, 8px); font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-primario, #f2f3f5); }
        .alerta-icone { flex-shrink: 0; width: 16px; height: 16px; }
        .atividades-lista { display: flex; flex-direction: column; gap: var(--pg-space-3, 12px); max-height: 320px; overflow-y: auto; }
        .atividade-item { display: flex; align-items: flex-start; gap: var(--pg-space-3, 12px); padding-bottom: var(--pg-space-3, 12px); border-bottom: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36); }
        .atividade-item:last-child { border-bottom: none; padding-bottom: 0; }
        .atividade-ponto { width: 8px; height: 8px; margin-top: 6px; border-radius: var(--pg-radius-full, 9999px); background: var(--pg-color-destaque, #2f8cff); flex-shrink: 0; }
        .atividade-corpo { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .atividade-quem { font-size: var(--pg-font-size-sm, 0.875rem); font-weight: var(--pg-font-weight-medio, 500); color: var(--pg-color-texto-primario, #f2f3f5); }
        .atividade-meta { display: flex; gap: 8px; font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); }
        /* pg-table não tem rolagem própria (largura de coluna cresce com o conteúdo) — "Últimos
           clientes" (4 colunas, com a nova coluna "Pedidos") e "Pedidos recentes" (5 colunas) são
           largas o bastante pra estourar a largura do card em mobile; a rolagem fica contida aqui
           dentro do próprio card, nunca a página inteira. min-width zero é necessário nos dois
           níveis porque a seção é um item flex do body de pg-card (display flex) — sem
           isso, o item flex nunca encolhe abaixo do conteúdo, e overflow-x auto não tem efeito
           nenhum (o card inteiro cresce em vez de rolar). */
        #secao-clientes-recentes, #secao-pedidos-recentes { min-width: 0; }
        .tabela-scroll { overflow-x: auto; min-width: 0; }
        /* Qualquer pg-empty-state dentro da coluna lateral do gráfico ou da linha
           participação/bônus (carregando, vazio, erro, "indisponível" — todos os estados
           possíveis, sejam estáticos no HTML ou injetados depois por renderVazio()/
           renderSecaoEstado()) — o padding interno padrão do componente (32px, pensado pra
           ocupar uma seção inteira sozinha) deixava esses cards menores/compactos
           desproporcionalmente altos para o pouco conteúdo real que mostram (ícone + 1-2
           frases). Seletor por CONTAINER PAI (não por classe no elemento) pra cobrir os dois
           casos sem precisar lembrar de marcar cada pg-empty-state criado dinamicamente em
           JS. ::part(estado) é a única forma de alcançar o miolo do componente através do
           shadow DOM dele (exposto de propósito via part="estado" em pg-empty-state.ts) —
           reduz só o padding, sem duplicar/reimplementar o componente. */
        .area-principal-lateral pg-empty-state::part(estado),
        .secoes-societario pg-empty-state::part(estado) {
          padding: var(--pg-space-4, 16px);
          gap: var(--pg-space-1, 4px);
        }
        ${CSS_SITE_TRAFEGO}
      </style>
      <div class="cabecalho">
        <div>
          <h1><span>Olá</span>, ${escaparHtml(primeiroNome(this._nomeUsuario))}</h1>
          <p class="sub">Visão geral da PG Custom — faturamento, sua participação e bônus.</p>
        </div>
        <select class="seletor-periodo" id="seletor-periodo" title="Período do gráfico de evolução financeira">
          ${OPCOES_PERIODO.map((m) => `<option value="${m}" ${m === this.periodoMeses ? 'selected' : ''}>Últimos ${m} meses</option>`).join('')}
        </select>
      </div>

      <div class="kpis">
        <pg-kpi-card id="kpi-pedidos-hoje" label="Pedidos novos (hoje)" icone="caixa" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-faturamento" label="Faturamento (mês)" icone="dinheiro" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-faturamento-ano" label="Faturamento anual" icone="dinheiro" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-lucro-mes" label="Lucro do mês" icone="tendencia" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-seu-lucro" label="Seu lucro" icone="dinheiro" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-comissao-tecnica" label="Sua comissão técnica" icone="producao" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-bonus" label="Bônus PG recebido" icone="parceiros" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-pedidos" label="Pedidos pagos (mês)" icone="carrinho" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-parceiros-ativos" label="Parceiros ativos" icone="parceiros" estado="carregando"></pg-kpi-card>
      </div>

      <div class="area-principal">
        <pg-card padding="compacto">
          <span slot="header">Evolução financeira</span>
          <div id="secao-evolucao"><pg-empty-state variant="carregando" titulo="Evolução financeira"></pg-empty-state></div>
        </pg-card>
        <div class="area-principal-lateral">
          <pg-card padding="compacto">
            <span slot="header">Clientes &amp; Cupons</span>
            <div id="secao-clientes-resumo"><pg-empty-state variant="carregando" titulo="Clientes &amp; Cupons"></pg-empty-state></div>
          </pg-card>
          <pg-card padding="compacto">
            <span slot="header">Cupons mais usados</span>
            <div id="secao-cupons-mais-usados"><pg-empty-state variant="carregando" titulo="Cupons mais usados"></pg-empty-state></div>
          </pg-card>
          <pg-card padding="compacto">
            <span slot="header">Alertas</span>
            <div id="secao-alertas-resumo"><pg-empty-state variant="carregando" titulo="Alertas"></pg-empty-state></div>
            <span slot="footer" class="link-rodape">Ver todos os alertas →</span>
          </pg-card>
          <pg-card padding="compacto">
            <span slot="header">Próximo vencimento</span>
            ${htmlProximoVencimento()}
            <span slot="footer" class="link-rodape">Ver detalhes →</span>
          </pg-card>
        </div>
      </div>

      ${HTML_CARD_SITE_TRAFEGO}
      ${HTML_CARD_VENDAS_ORIGEM}

      <div class="secoes-societario">
        <pg-card padding="compacto">
          <span slot="header">Metas</span>
          <div id="secao-metas"><pg-empty-state variant="carregando" titulo="Metas"></pg-empty-state></div>
        </pg-card>
        <pg-card padding="compacto">
          <span slot="header">Próximos pagamentos</span>
          ${htmlProximosPagamentosLista()}
        </pg-card>
      </div>

      <div class="donuts-kpi">
        <pg-donut-kpi id="donut-participacao" titulo="Participação societária" cor="roxo" estado="carregando"></pg-donut-kpi>
        <pg-donut-kpi id="donut-bonus" titulo="Bônus PG" cor="dourado" estado="carregando"></pg-donut-kpi>
        <pg-donut-kpi id="donut-pedidos" titulo="Pedidos pagos (mês)" cor="azul" estado="carregando"></pg-donut-kpi>
        <pg-donut-kpi id="donut-parceiros" titulo="Parceiros ativos" cor="verde" estado="carregando"></pg-donut-kpi>
        <pg-donut-kpi id="donut-ticket-medio" titulo="Ticket médio (mês)" cor="laranja" estado="carregando"></pg-donut-kpi>
        ${HTML_DONUT_CONVERSAO}
      </div>

      <div class="secoes operacional">
        <pg-card>
          <span slot="header">Atividades recentes</span>
          <div id="secao-atividades"><pg-empty-state variant="carregando" titulo="Atividades recentes"></pg-empty-state></div>
        </pg-card>
        <pg-card>
          <span slot="header">Últimos clientes</span>
          <div id="secao-clientes-recentes"><pg-empty-state variant="carregando" titulo="Últimos clientes"></pg-empty-state></div>
        </pg-card>
      </div>

      <pg-card class="pedidos-recentes-full">
        <span slot="header">Pedidos recentes</span>
        <div id="secao-pedidos-recentes"><pg-empty-state variant="carregando" titulo="Pedidos recentes"></pg-empty-state></div>
        <a slot="footer" href="#/pedidos" class="link-rodape">Ver todos os pedidos →</a>
      </pg-card>
    `;

    this.root.querySelector('#seletor-periodo')?.addEventListener('change', (evento) => {
      const valor = Number((evento.target as HTMLSelectElement).value);
      if (!OPCOES_PERIODO.includes(valor as (typeof OPCOES_PERIODO)[number])) return;
      this.periodoMeses = valor as (typeof OPCOES_PERIODO)[number];
      void this.carregarEvolucaoFinanceira();
    });
  }

  private async carregar(): Promise<void> {
    await Promise.all([
      this.carregarFinanceiro(),
      this.carregarSocietario(),
      this.carregarEvolucaoFinanceira(),
      this.carregarParceiros(),
      this.carregarResumoClientes(),
      this.carregarCuponsMaisUsados(),
      this.carregarClientesRecentes(),
      this.carregarPedidosOperacional(),
      this.carregarAvisos(),
      this.carregarCentralDeComando(),
      this.carregarAtividadesRecentes(),
    ]);
  }

  /** Mesmo dado/padrão de `dashboard.page.ts` (`GET /financeiro/faturamento-por-periodo`) — admin tem RBAC completo em `financeiro`. */
  private async carregarEvolucaoFinanceira(): Promise<void> {
    if (!this._financeiroApi) return;
    try {
      const resultado = await this._financeiroApi.obterFaturamentoPorPeriodo(this.periodoMeses);
      const body = this.root.querySelector('#secao-evolucao');
      if (!body) return;

      if (resultado.status !== 'ok') {
        this.renderSecaoEstado('secao-evolucao', 'Evolução financeira', resultado);
        return;
      }
      if (resultado.dado.length === 0) {
        body.innerHTML = `<pg-empty-state variant="vazio" titulo="Evolução financeira" descricao="nenhuma transação registrada no período"></pg-empty-state>`;
        return;
      }

      body.innerHTML = '<pg-chart></pg-chart>';
      const chart = body.querySelector('pg-chart') as PgChart;
      chart.type = 'line';
      chart.series = [
        { key: 'faturamento', label: 'Faturamento', color: 'var(--pg-color-sucesso)' },
        { key: 'lucro', label: 'Lucro', color: 'var(--pg-color-roxo)' },
      ];
      chart.formatarValor = (centavos: number) => formatBRL(centavosParaReais(centavos));
      // A API só devolve os meses que TÊM alguma transação real (agregação por GROUP BY) — sem
      // completar aqui os meses do próprio período selecionado sem nenhuma movimentação, o
      // gráfico de evolução recebia só 1-2 pontos soltos em vez de uma série mensal completa (daí
      // a linha "achatada"/quase reta: com 1 ponto só, `pg-chart` nem desenha evolução, só uma
      // referência pontilhada). `preencherMesesSemMovimentacao` projeta `resultado.dado` sobre a
      // janela EXATA de `this.periodoMeses` meses (cronológica, terminando no mês corrente — a
      // mesma janela que o seletor "Últimos N meses" promete), com R$ 0,00 real nos meses sem
      // transação. Isolado só ao gráfico: a tendência/sparkline dos KPIs abaixo continua usando
      // `resultado.dado` original, sem este preenchimento.
      const dadoCompleto = preencherMesesSemMovimentacao(resultado.dado, this.periodoMeses);
      chart.points = dadoCompleto.map((p: FaturamentoPeriodo) => ({
        label: formatarRotuloPeriodo(p.periodo),
        values: { faturamento: p.receitaCentavos, lucro: p.lucroEstimadoCentavos },
      }));

      // Tendência/sparkline dos KPIs "Faturamento (mês)"/"Lucro do mês" — mesma série já buscada
      // aqui pra desenhar o gráfico, reaproveitada (nenhuma chamada nova, nenhum número inventado;
      // mesmo padrão/fórmula de `dashboard.page.ts`, `calcularTendenciaEPontos`). Roda em paralelo
      // a `carregarFinanceiro()` (quem define `valor`/`estado` desses cards) sem conflito: só mexe
      // em `tendencia`/sparkline, nunca em `valor`/`estado`.
      const { tendencia: tFaturamento, tendenciaDirecao: dFaturamento, pontos: pFaturamento } =
        calcularTendenciaEPontos(resultado.dado.map((p) => p.receitaCentavos));
      this.definirTendencia('kpi-faturamento', tFaturamento, dFaturamento, pFaturamento, 'var(--pg-color-sucesso)');
      const { tendencia: tLucro, tendenciaDirecao: dLucro, pontos: pLucro } =
        calcularTendenciaEPontos(resultado.dado.map((p) => p.lucroEstimadoCentavos));
      this.definirTendencia('kpi-lucro-mes', tLucro, dLucro, pLucro, 'var(--pg-color-roxo)');
    } catch (erro) {
      logger.erro('falha ao carregar evolução financeira (dashboard CEO)', { erro });
      this.renderSecaoEstado('secao-evolucao', 'Evolução financeira', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  private async carregarParceiros(): Promise<void> {
    if (!this._parceirosApi) return;
    try {
      const resultado = await this._parceirosApi.listar();
      if (resultado.status !== 'ok') {
        this.aplicarEstadoKpi('kpi-parceiros-ativos', resultado);
        this.aplicarEstadoDonut('donut-parceiros', legendaDoResultado(resultado));
        if (resultado.status === 'vazio') {
          this.definirKpi('kpi-parceiros-ativos', '0');
          this.definirDonut('donut-parceiros', '0', { percentual: 0, dica: 'nenhum parceiro cadastrado' });
        }
        return;
      }
      const total = resultado.dado.length;
      const ativos = resultado.dado.filter((p) => p.ativo).length;
      this.definirKpi('kpi-parceiros-ativos', String(ativos));
      // Percentual real (ativos/total cadastrados) — nunca inventado, só a mesma lista já buscada aqui.
      const percentualAtivos = total > 0 ? Math.round((ativos / total) * 100) : 0;
      this.definirDonut('donut-parceiros', String(ativos), {
        percentual: percentualAtivos,
        dica: total > 0 ? `${ativos} de ${total} parceiros` : 'nenhum parceiro cadastrado',
      });
    } catch (erro) {
      logger.erro('falha ao carregar parceiros (dashboard CEO)', { erro });
      this.aplicarEstadoKpi('kpi-parceiros-ativos', { status: 'erro', mensagem: 'falha ao carregar' });
      this.aplicarEstadoDonut('donut-parceiros', 'falha ao carregar');
    }
  }

  private async carregarFinanceiro(): Promise<void> {
    if (!this._relatoriosApi) return;
    try {
      const resultado = await this._relatoriosApi.obterPulsoDoNegocio();
      if (resultado.status !== 'ok') {
        this.aplicarEstadoKpi('kpi-faturamento', resultado);
        this.aplicarEstadoKpi('kpi-faturamento-ano', resultado);
        this.aplicarEstadoKpi('kpi-lucro-mes', resultado);
        this.aplicarEstadoKpi('kpi-pedidos', resultado);
        this.aplicarEstadoKpi('kpi-seu-lucro', resultado);
        this.aplicarEstadoDonut('donut-pedidos', legendaDoResultado(resultado));
        this.aplicarEstadoDonut('donut-ticket-medio', legendaDoResultado(resultado));
        this.renderSecaoEstado('secao-metas', 'Metas', resultado);
        this.renderSecaoEstado('secao-alertas-resumo', 'Alertas', resultado);
        return;
      }
      const { receitaMesCentavos, receitaAnoCentavos, lucroMesCentavos, pedidosPagosMes, ticketMedioMesCentavos } = resultado.dado;
      this.definirKpi('kpi-faturamento', formatBRL(centavosParaReais(receitaMesCentavos)));
      // "Faturamento anual" — mesma fonte financeira (`PulsoNegocio.receitaAnoCentavos`, receita
      // acumulada do ano-calendário no Postgres), nunca a soma dos meses do gráfico (que segue o
      // seletor de período 3/6/12m, não o ano).
      this.definirKpi('kpi-faturamento-ano', formatBRL(centavosParaReais(receitaAnoCentavos)));
      this.definirKpi('kpi-lucro-mes', formatBRL(centavosParaReais(lucroMesCentavos)));
      this.definirKpi('kpi-pedidos', String(pedidosPagosMes));
      this.lucroMesCentavos = lucroMesCentavos;
      this.atualizarSeuLucro();

      // Sem denominador real pra virar proporção (não existe "meta de pedidos" nem histórico
      // mensal de contagem carregado aqui) — o anel fica só com a trilha, nunca um percentual inventado.
      this.definirDonut('donut-pedidos', String(pedidosPagosMes));
      // Mesma regra de "0 pedidos pagos" de `dashboard.page.ts`, linha do ticket médio: sem pedido pago
      // no mês, o ticket médio real é 0, não "indisponível" (a query nunca dividiria por zero pra chegar lá).
      const ticketMedio = pedidosPagosMes > 0 ? formatBRL(centavosParaReais(ticketMedioMesCentavos)) : formatBRL(0);
      this.definirDonut('donut-ticket-medio', ticketMedio);

      // "Metas" (linha de destaque) e "Alertas" (coluna lateral) — sobre o MESMO pedidosPagosMes
      // acima (nunca uma 2ª consulta/definição).
      this.renderMetaProgresso(pedidosPagosMes);
      this.renderAlertasResumo(pedidosPagosMes);
    } catch (erro) {
      logger.erro('falha ao carregar pulso do negócio (dashboard CEO)', { erro });
      this.aplicarEstadoKpi('kpi-faturamento', { status: 'erro', mensagem: 'falha ao carregar' });
      this.aplicarEstadoKpi('kpi-faturamento-ano', { status: 'erro', mensagem: 'falha ao carregar' });
      this.aplicarEstadoKpi('kpi-lucro-mes', { status: 'erro', mensagem: 'falha ao carregar' });
      this.aplicarEstadoKpi('kpi-pedidos', { status: 'erro', mensagem: 'falha ao carregar' });
      this.aplicarEstadoDonut('donut-pedidos', 'falha ao carregar');
      this.aplicarEstadoDonut('donut-ticket-medio', 'falha ao carregar');
      this.renderSecaoEstado('secao-metas', 'Metas', { status: 'erro', mensagem: 'falha ao carregar' });
      this.renderSecaoEstado('secao-alertas-resumo', 'Alertas', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  /**
   * "Metas" (linha de destaque, substitui "Sua participação societária") — barra de progresso
   * da meta fixa de 14 pedidos pagos/mês (`META_PEDIDOS_PAGOS_MES`), sobre o MESMO
   * `pedidosPagosMes` do KPI/donut "Pedidos pagos (mês)" (nenhuma 2ª consulta/definição). Mês/ano
   * do rótulo vem do relógio local — a meta e a contagem são sempre do mês corrente, nunca de um
   * período selecionável (`periodoMeses` só controla o gráfico de Evolução financeira).
   */
  private renderMetaProgresso(pedidosPagosMes: number): void {
    const body = this.root.querySelector('#secao-metas');
    if (!body) return;
    const progresso = Math.min(1, pedidosPagosMes / META_PEDIDOS_PAGOS_MES);
    const atingida = pedidosPagosMes >= META_PEDIDOS_PAGOS_MES;
    const faltam = META_PEDIDOS_PAGOS_MES - pedidosPagosMes;
    body.innerHTML = `
      <div class="meta-card">
        <span class="meta-periodo">${escaparHtml(rotuloMesAtual())}</span>
        <div class="meta-linha">
          <span class="meta-rotulo">Pedidos pagos</span>
          <span class="meta-percentual">${formatarPercentualMeta(progresso)}</span>
        </div>
        <span class="meta-valor">${pedidosPagosMes} / ${META_PEDIDOS_PAGOS_MES} pedidos</span>
        <div class="meta-barra"><div class="meta-barra-preenchimento" style="width:${(progresso * 100).toFixed(2)}%"></div></div>
        <span class="meta-status${atingida ? ' atingida' : ''}">${atingida ? 'Meta atingida' : rotuloFaltamPedidos(faltam)}</span>
      </div>`;
  }

  /**
   * "Alertas" (coluna lateral do gráfico) — resumo textual de pendências reais, combinando a
   * MESMA meta de pedidos do card "Metas" (sobre o MESMO `pedidosPagosMes`, nenhuma 2ª
   * consulta/definição) com a MESMA despesa fixa mais próxima de vencer do card "Próximo
   * vencimento"/"Próximos pagamentos" (`proximasDespesasOrdenadas`). Nunca duplica a lógica de
   * avisos operacionais (OPS sem técnico, comissões pendentes etc. — isso é `renderAlertas()`/
   * `#secao-alertas`, uma fonte totalmente diferente). Sem pendência real nenhuma (meta já
   * atingida e nenhuma despesa fixa cadastrada), mostra um estado vazio coerente.
   */
  private renderAlertasResumo(pedidosPagosMes: number): void {
    const body = this.root.querySelector('#secao-alertas-resumo');
    if (!body) return;

    const itens: string[] = [];
    if (pedidosPagosMes < META_PEDIDOS_PAGOS_MES) {
      itens.push(`${rotuloFaltamPedidos(META_PEDIDOS_PAGOS_MES - pedidosPagosMes)} para atingir a meta`);
    }
    const proximas = proximasDespesasOrdenadas();
    if (proximas.length > 0) {
      const { despesa, vencimento } = proximas[0]!;
      const rotulo = rotuloDiasRestantes(diasAteVencimento(vencimento));
      itens.push(`${despesa.nome} ${rotulo.charAt(0).toLowerCase()}${rotulo.slice(1)}`);
    }

    if (itens.length === 0) {
      body.innerHTML = `<pg-empty-state variant="vazio" titulo="Alertas" descricao="nenhuma pendência no momento"></pg-empty-state>`;
      return;
    }

    body.innerHTML = `
      <div class="alertas-resumo">
        <span class="alertas-resumo-contagem">${itens.length} pendência${itens.length === 1 ? '' : 's'}</span>
        <ul class="alertas-resumo-lista">
          ${itens.map((item) => `<li>${escaparHtml(item)}</li>`).join('')}
        </ul>
      </div>`;
  }

  private lucroMesCentavos: number | null = null;
  private percentualParticipacao: number | null = null;

  private atualizarSeuLucro(): void {
    if (this.lucroMesCentavos === null) return;
    if (this.percentualParticipacao === null) {
      // Sem participação ativa, o valor real (não inventado) É R$ 0,00 — 0% de qualquer lucro do
      // mês é sempre zero. `estado="ok"` normal (não "indisponivel") para caber num rótulo curto
      // de 1 linha só, igual aos demais KPIs — "indisponivel" forçava um "—" + dica longa, o que
      // deixava este card visualmente mais alto que os outros 7 da fileira.
      this.definirKpi('kpi-seu-lucro', formatBRL(0), 'Sem participação societária ativa');
      return;
    }
    const seuLucro = Math.round(this.lucroMesCentavos * (this.percentualParticipacao / 100));
    this.definirKpi('kpi-seu-lucro', formatBRL(centavosParaReais(seuLucro)), `${this.percentualParticipacao}% do lucro do mês`);
  }

  private async carregarSocietario(): Promise<void> {
    if (!this._societarioApi) return;
    try {
      const resumo = await this._societarioApi.meuResumo();

      if (resumo.participacaoAtiva) {
        this.percentualParticipacao = resumo.participacaoAtiva.percentual;
        this.atualizarSeuLucro();
        const body = this.root.querySelector('#secao-participacao');
        if (body) {
          body.innerHTML = `
            <div style="display:flex;flex-direction:column;gap:8px;font-size:var(--pg-font-size-sm,0.875rem);color:var(--pg-color-texto-primario,#f2f3f5);">
              <div>Participação ativa: <strong>${resumo.participacaoAtiva.percentual}%</strong></div>
              <div style="color:var(--pg-color-texto-secundario,#9aa1ab);">desde ${new Date(resumo.participacaoAtiva.inicioEm).toLocaleDateString('pt-BR')}</div>
            </div>
          `;
        }
        this.definirDonut('donut-participacao', `${resumo.participacaoAtiva.percentual}%`, {
          percentual: resumo.participacaoAtiva.percentual,
          dica: `desde ${new Date(resumo.participacaoAtiva.inicioEm).toLocaleDateString('pt-BR')}`,
        });
      } else {
        // `percentualParticipacao` continua `null` (nenhuma participação ativa) — mesma fonte
        // única de `atualizarSeuLucro()` usada acima (evita 2 lugares escrevendo a mesma
        // mensagem de "sem participação" com textos que podiam divergir).
        this.atualizarSeuLucro();
        this.renderVazio('secao-participacao', 'Participação', 'nenhuma participação societária ativa registrada para você');
        // Percentual real é 0 (nenhuma participação ativa) — dado real, não estado "vazio" do
        // componente: mostra "0%" explicitamente em vez de esconder o anel (regra do card: nunca
        // inventar um percentual, mas também nunca esconder um zero legítimo).
        this.definirDonut('donut-participacao', '0%', { percentual: 0, dica: 'nenhuma participação societária ativa registrada' });
      }

      const bonusValidoCentavos = resumo.bonus
        .filter((b) => b.status !== 'rejeitado' && b.status !== 'cancelado')
        .reduce((soma, b) => soma + b.valorCentavos, 0);
      const bonusPagoCentavos = resumo.bonus.filter((b) => b.status === 'pago').reduce((soma, b) => soma + b.valorCentavos, 0);
      this.definirKpi('kpi-bonus', formatBRL(centavosParaReais(bonusPagoCentavos)));

      // Percentual do anel: fração já paga do bônus vigente (exclui rejeitado/cancelado do
      // denominador — não é bônus que vai ser pago). Sem bônus nenhum, fica 0% real (nunca inventado).
      const percentualBonusPago = bonusValidoCentavos > 0 ? Math.round((bonusPagoCentavos / bonusValidoCentavos) * 100) : 0;
      this.definirDonut('donut-bonus', formatBRL(centavosParaReais(bonusPagoCentavos)), {
        percentual: percentualBonusPago,
        dica: resumo.bonus.length > 0 ? `${percentualBonusPago}% do bônus vigente já pago` : 'nenhum bônus PG registrado ainda',
      });

      if (resumo.bonus.length === 0) {
        this.renderVazio('secao-bonus', 'Bônus PG', 'nenhum bônus PG registrado para você ainda');
      } else {
        const body = this.root.querySelector('#secao-bonus');
        if (body) {
          const colunas: PgTableColumn[] = [
            { key: 'competencia', label: 'Competência' },
            { key: 'motivo', label: 'Motivo' },
            { key: 'valor', label: 'Valor', align: 'right' },
            { key: 'status', label: 'Status' },
          ];
          const linhas = resumo.bonus.map((b) => ({
            competencia: b.competencia,
            motivo: b.motivo,
            valor: formatBRL(centavosParaReais(b.valorCentavos)),
            status: STATUS_BONUS_BADGE[b.status] ?? { texto: b.status, tom: 'neutro' as const },
          }));
          body.innerHTML = '<pg-table></pg-table>';
          const tabela = body.querySelector('pg-table') as PgTable;
          tabela.columns = colunas;
          tabela.rows = linhas;
        }
      }
    } catch (erro) {
      logger.erro('falha ao carregar resumo societário (dashboard CEO)', { erro });
      this.aplicarEstadoKpi('kpi-seu-lucro', { status: 'erro', mensagem: 'falha ao carregar' });
      this.aplicarEstadoKpi('kpi-bonus', { status: 'erro', mensagem: 'falha ao carregar' });
      this.aplicarEstadoDonut('donut-participacao', 'falha ao carregar');
      this.aplicarEstadoDonut('donut-bonus', 'falha ao carregar');
    }

    if (this._comissoesApi) {
      try {
        const minhas: Resultado<Comissao[]> = await this._comissoesApi.minhas('tecnico');
        if (minhas.status === 'ok') {
          const total = minhas.dado.reduce((soma, c) => soma + c.valorCentavos, 0);
          this.definirKpi('kpi-comissao-tecnica', formatBRL(centavosParaReais(total)));
        } else if (minhas.status === 'vazio') {
          this.definirKpi('kpi-comissao-tecnica', formatBRL(0));
        } else {
          this.aplicarEstadoKpi('kpi-comissao-tecnica', minhas);
        }
      } catch (erro) {
        logger.erro('falha ao carregar comissão técnica pessoal (dashboard CEO)', { erro });
        this.aplicarEstadoKpi('kpi-comissao-tecnica', { status: 'erro', mensagem: 'falha ao carregar' });
      }
    }
  }

  /** "Clientes & Cupons" — mesma fonte de `RelatorioService.obterResumoClientes` (`comercial.clientes`/`comercial.pedidos`). Sem "taxa de conversão": fórmula não definida em nenhum documento do projeto. */
  private async carregarResumoClientes(): Promise<void> {
    if (!this._relatoriosApi) return;
    try {
      const resultado = await this._relatoriosApi.obterResumoClientes();
      if (resultado.status !== 'ok') {
        this.renderSecaoEstado('secao-clientes-resumo', 'Clientes & Cupons', resultado);
        return;
      }
      const body = this.root.querySelector('#secao-clientes-resumo');
      if (!body) return;
      body.innerHTML = `
        <div style="display:flex;flex-direction:column;gap:8px;font-size:var(--pg-font-size-sm,0.875rem);color:var(--pg-color-texto-primario,#f2f3f5);">
          <div style="font-family:var(--pg-font-familia-display,system-ui);font-size:var(--pg-font-size-2xl,2rem);font-weight:var(--pg-font-weight-bold,700);">${resultado.dado.totalClientes}</div>
          <div style="color:var(--pg-color-texto-secundario,#9aa1ab);">Clientes cadastrados</div>
          <div>Novos no mês: <strong>${resultado.dado.novosClientesMes}</strong></div>
          <div>Pedidos com cupom (mês): <strong>${resultado.dado.pedidosComCupomMes}</strong></div>
        </div>`;
    } catch (erro) {
      logger.erro('falha ao carregar resumo de clientes (dashboard CEO)', { erro });
      this.renderSecaoEstado('secao-clientes-resumo', 'Clientes & Cupons', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  /** "Cupons mais usados" — top 5 real por contagem de pedidos, mesma fonte de `dashboard.page.ts`, sem filtro de período (ranking histórico). */
  private async carregarCuponsMaisUsados(): Promise<void> {
    if (!this._relatoriosApi) return;
    try {
      const resultado = await this._relatoriosApi.obterCuponsMaisUsados(5);
      const body = this.root.querySelector('#secao-cupons-mais-usados');
      if (!body) return;
      if (resultado.status !== 'ok') {
        this.renderSecaoEstado('secao-cupons-mais-usados', 'Cupons mais usados', resultado);
        return;
      }
      body.innerHTML = '<pg-chart></pg-chart>';
      const chart = body.querySelector('pg-chart') as PgChart;
      chart.type = 'bar';
      chart.series = [{ key: 'usos', label: 'Usos' }];
      chart.formatarValor = (valor: number) => String(valor);
      chart.points = resultado.dado.map((c: CupomMaisUsado): PgChartPoint => ({ label: c.codigo, values: { usos: c.usos } }));
    } catch (erro) {
      logger.erro('falha ao carregar cupons mais usados (dashboard CEO)', { erro });
      this.renderSecaoEstado('secao-cupons-mais-usados', 'Cupons mais usados', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  /**
   * "Últimos clientes" — mesmo endpoint de `<erp-clientes-page>`
   * (`ClientesApi.listar`), ordenado por `criado_em DESC` no backend;
   * aqui só limita a 5. Só GUARDA a lista e delega a renderização a
   * `renderUltimosClientesTabela` — essa função roda de novo quando
   * `carregarPedidosOperacional` (fonte independente, em paralelo)
   * terminar, para acrescentar a contagem real de pedidos por cliente
   * sem bloquear a tabela nem inventar um "0" antes da resposta chegar.
   */
  private async carregarClientesRecentes(): Promise<void> {
    if (!this._clientesApi) return;
    try {
      const resultado = await this._clientesApi.listar({ limit: 5 });
      if (resultado.status === 'vazio') {
        this.clientesRecentesLista = [];
        this.renderUltimosClientesTabela();
        return;
      }
      if (resultado.status !== 'ok') {
        this.renderSecaoEstado('secao-clientes-recentes', 'Últimos clientes', resultado);
        return;
      }
      this.clientesRecentesLista = resultado.dado;
      this.renderUltimosClientesTabela();
    } catch (erro) {
      logger.erro('falha ao carregar últimos clientes (dashboard CEO)', { erro });
      this.renderSecaoEstado('secao-clientes-recentes', 'Últimos clientes', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  /**
   * Recombina `clientesRecentesLista` (obrigatória) com
   * `pedidosRecentesTodos` (opcional, mesma lista já buscada para o KPI
   * "Pedidos novos (hoje)"/"Pedidos recentes" — nenhuma chamada nova só
   * pra isso). Sem a lista de pedidos ainda, a coluna mostra "…"
   * (carregando), nunca "0 pedidos" — um zero antes da resposta real
   * chegar seria um dado inventado, mesmo que por um instante.
   */
  private renderUltimosClientesTabela(): void {
    if (this.clientesRecentesLista === null) return;
    const body = this.root.querySelector('#secao-clientes-recentes');
    if (!body) return;
    if (this.clientesRecentesLista.length === 0) {
      body.innerHTML = `<pg-empty-state variant="vazio" titulo="Últimos clientes" descricao="nenhum cliente cadastrado ainda"></pg-empty-state>`;
      return;
    }
    const contagemPorCliente = this.pedidosRecentesTodos ? contarPedidosPorCliente(this.pedidosRecentesTodos) : null;
    const colunas: PgTableColumn[] = [
      { key: 'nome', label: 'Nome' },
      { key: 'email', label: 'E-mail' },
      { key: 'criadoEm', label: 'Cadastrado em' },
      { key: 'pedidos', label: 'Pedidos', align: 'right' },
    ];
    const linhas = this.clientesRecentesLista.map((cliente: Cliente) => ({
      nome: cliente.nome,
      email: cliente.email ?? '—',
      criadoEm: formatarDataHora(cliente.criadoEm),
      pedidos: contagemPorCliente
        ? rotuloContagemPedidos(contagemPorCliente.get(cliente.id) ?? 0)
        : this.pedidosOperacionalIndisponivel
          ? '—'
          : '…',
    }));
    body.innerHTML = '<div class="tabela-scroll"><pg-table></pg-table></div>';
    const tabela = body.querySelector('pg-table') as PgTable;
    tabela.columns = colunas;
    tabela.rows = linhas;
  }

  /**
   * Fonte única para 3 recursos novos, nenhuma chamada extra por seção:
   * `DashboardApi.obterPedidosPorStatus` (mesmo método já usado por
   * `dashboard.page.ts`) devolve a lista completa de pedidos mais
   * recentes (até 100, `criado_em DESC` no backend) — dela derivamos o
   * KPI "Pedidos novos (hoje)" (contagem real por dia corrente,
   * comparação com ontem só quando ontem teve pelo menos 1 pedido, nunca
   * dividindo por zero), a tabela "Pedidos recentes" (top 5) e a
   * contagem de pedidos por cliente de "Últimos clientes".
   */
  private async carregarPedidosOperacional(): Promise<void> {
    if (!this._dashboardApi) return;
    try {
      const resultado = await this._dashboardApi.obterPedidosPorStatus();
      if (resultado.status !== 'ok') {
        this.aplicarEstadoKpi('kpi-pedidos-hoje', resultado);
        this.pedidosOperacionalIndisponivel = true;
        this.renderSecaoEstado('secao-pedidos-recentes', 'Pedidos recentes', resultado);
        this.renderUltimosClientesTabela();
        return;
      }

      const pedidos = resultado.dado.pedidos;
      this.pedidosRecentesTodos = pedidos;

      const hoje = new Date();
      const ontem = new Date(hoje);
      ontem.setDate(hoje.getDate() - 1);
      const pedidosHoje = pedidos.filter((p) => mesmoDiaLocal(new Date(p.criadoEm), hoje)).length;
      const pedidosOntem = pedidos.filter((p) => mesmoDiaLocal(new Date(p.criadoEm), ontem)).length;

      this.definirKpi('kpi-pedidos-hoje', String(pedidosHoje), resultado.dado.aproximado ? '100+ pedidos na primeira página' : undefined);
      if (pedidosOntem > 0) {
        const variacao = ((pedidosHoje - pedidosOntem) / pedidosOntem) * 100;
        const direcao: 'alta' | 'baixa' | 'neutra' = variacao > 0.5 ? 'alta' : variacao < -0.5 ? 'baixa' : 'neutra';
        const card = this.root.querySelector('#kpi-pedidos-hoje');
        card?.setAttribute('tendencia', formatarPercentual(variacao));
        card?.setAttribute('tendencia-direcao', direcao);
      }

      const recentes = pedidos.slice(0, 5);
      const body = this.root.querySelector('#secao-pedidos-recentes');
      if (body) {
        if (recentes.length === 0) {
          body.innerHTML = `<pg-empty-state variant="vazio" titulo="Pedidos recentes" descricao="nenhum pedido cadastrado ainda"></pg-empty-state>`;
        } else {
          const produtosPorPedido = await this.resolverProdutosDosPedidos(recentes.map((p) => p.id));
          const colunas: PgTableColumn[] = [
            { key: 'pedido', label: 'Produto' },
            { key: 'status', label: 'Status' },
            { key: 'pagamento', label: 'Pagamento' },
            { key: 'valor', label: 'Valor', align: 'right' },
            { key: 'criado', label: 'Criado' },
          ];
          body.innerHTML = '<div class="tabela-scroll"><pg-table></pg-table></div>';
          const tabela = body.querySelector('pg-table') as PgTable;
          tabela.columns = colunas;
          tabela.rows = recentes.map((pedido) => pedidoParaLinhaOperacional(pedido, produtosPorPedido.get(pedido.id)));
        }
      }

      this.renderUltimosClientesTabela();
    } catch (erro) {
      logger.erro('falha ao carregar pedidos (dashboard CEO)', { erro });
      this.aplicarEstadoKpi('kpi-pedidos-hoje', { status: 'erro', mensagem: 'falha ao carregar' });
      this.pedidosOperacionalIndisponivel = true;
      this.renderSecaoEstado('secao-pedidos-recentes', 'Pedidos recentes', { status: 'erro', mensagem: 'falha ao carregar' });
      this.renderUltimosClientesTabela();
    }
  }

  /**
   * Nome real do(s) produto(s) de cada pedido, para a coluna "Produto" de
   * "Pedidos recentes". `PedidosApi.listar`/`obterPedidosPorStatus` (a
   * fonte de `pedidos`, acima) não trazem os itens do pedido — só
   * `GET /pedidos/:id` (`PedidosApi.obterPorId`) devolve
   * `PedidoComItens.itens`, cada um com `descricao` real (sincronizada do
   * `product_name` do pedido legado — `integracao-legado.repository`).
   * Busca individual só para os 5 pedidos exibidos na tabela (nunca para
   * os até 100 de `pedidosRecentesTodos`), em paralelo, best-effort: se
   * um pedido específico falhar (403/404), some do mapa e a linha cai no
   * fallback de `pedidoParaLinhaOperacional` — nunca um nome inventado.
   */
  private async resolverProdutosDosPedidos(pedidoIds: string[]): Promise<Map<string, string>> {
    const mapa = new Map<string, string>();
    if (!this._pedidosApi) return mapa;
    await Promise.all(
      pedidoIds.map(async (id) => {
        try {
          const pedido = await this._pedidosApi!.obterPorId(id);
          if (pedido.itens.length > 0) mapa.set(id, formatarProdutosPedido(pedido.itens));
        } catch (erro) {
          logger.erro('falha ao resolver produto de um pedido (dashboard CEO, Pedidos recentes)', { erro, pedidoId: id });
        }
      })
    );
    return mapa;
  }

  /** "Alertas" — combina `DashboardApi.obterAvisos` (pedidos aguardando pagamento, OPS sem técnico, suporte aberto) com `RelatoriosApi.obterCentralDeComando` (comissões pendentes, garantias vencendo, insumos abaixo do ponto de reposição). Mesmas duas fontes reais e mesma lógica de combinação de `dashboard.page.ts`. */
  private async carregarAvisos(): Promise<void> {
    if (!this._dashboardApi) return;
    try {
      this.avisosResultado = await this._dashboardApi.obterAvisos();
      this.renderAlertas();
    } catch (erro) {
      logger.erro('falha ao carregar avisos e pendências (dashboard CEO)', { erro });
      this.avisosResultado = { status: 'erro', mensagem: 'falha ao carregar' };
      this.renderAlertas();
    }
  }

  private async carregarCentralDeComando(): Promise<void> {
    if (!this._relatoriosApi) return;
    try {
      this.centralDeComandoResultado = await this._relatoriosApi.obterCentralDeComando();
      this.renderAlertas();
    } catch (erro) {
      logger.erro('falha ao carregar central de comando (dashboard CEO)', { erro });
      this.centralDeComandoResultado = { status: 'erro', mensagem: 'falha ao carregar' };
      this.renderAlertas();
    }
  }

  /**
   * Rastreia o `Resultado` de cada fonte, não só os itens — sem acesso a
   * NENHUMA das duas, mostra "sem permissão", nunca "nenhuma pendência"
   * (que afirmaria, incorreto, que não há pendência real quando na
   * verdade ela só não pôde ser vista). "Pedidos atrasados na produção"
   * e "pagamento expirado" (categorias citadas como referência) não têm
   * fonte real no schema atual (sem SLA/prazo em OPS, sem estado de
   * expiração em `comercial.pedidos.status`) — por isso não aparecem
   * aqui como categoria própria, em vez de mostrar um número inventado.
   */
  private renderAlertas(): void {
    const body = this.root.querySelector('#secao-alertas');
    if (!body) return;
    if (this.avisosResultado.status === 'carregando' || this.centralDeComandoResultado.status === 'carregando') return;

    const itens: Array<{ titulo: string; quantidade: number; tom: 'alerta' | 'erro' }> = [];
    let algumaFonteAcessivel = false;

    if (this.avisosResultado.status === 'ok') {
      algumaFonteAcessivel = true;
      itens.push(...this.avisosResultado.dado.map((a) => ({ titulo: a.titulo, quantidade: a.quantidade, tom: 'alerta' as const })));
    } else if (this.avisosResultado.status === 'vazio') {
      algumaFonteAcessivel = true;
    }

    if (this.centralDeComandoResultado.status === 'ok') {
      algumaFonteAcessivel = true;
      const c = this.centralDeComandoResultado.dado;
      if (c.comissoesPendentes > 0) itens.push({ titulo: 'Comissões pendentes de pagamento', quantidade: c.comissoesPendentes, tom: 'alerta' });
      if (c.garantiasVencendo !== null && c.garantiasVencendo > 0) itens.push({ titulo: 'Garantias vencendo', quantidade: c.garantiasVencendo, tom: 'erro' });
      if (c.insumosBaixoEstoque > 0) itens.push({ titulo: 'Insumos abaixo do ponto de reposição', quantidade: c.insumosBaixoEstoque, tom: 'erro' });
    } else if (this.centralDeComandoResultado.status === 'vazio') {
      algumaFonteAcessivel = true;
    }

    if (!algumaFonteAcessivel) {
      body.innerHTML = `<pg-empty-state variant="sem-permissao" titulo="Alertas" descricao="você não tem permissão para ver este dado"></pg-empty-state>`;
      return;
    }
    if (itens.length === 0) {
      body.innerHTML = `<pg-empty-state variant="vazio" titulo="Alertas" descricao="nenhuma pendência operacional no momento"></pg-empty-state>`;
      return;
    }

    body.innerHTML = `
      <div class="alertas-lista">
        ${itens
          .map(
            (item) => `
          <div class="alerta-item">
            <span class="alerta-titulo">${svgAlertaIcone(item.tom)}${escaparHtml(item.titulo)}</span>
            <pg-badge tone="${item.tom}">${item.quantidade}</pg-badge>
          </div>`
          )
          .join('')}
      </div>`;
  }

  /**
   * "Atividades recentes" — `DashboardApi.obterAtividadesRecentes`
   * (`GET /auditoria`, mesmo endpoint de `dashboard.page.ts`): registro
   * automático de TODO evento de domínio publicado no barramento
   * (`core/audit`), nunca uma atividade fabricada. `ator` é o id bruto
   * de quem publicou o evento (ou `'system'`/`'scheduler'`) — nunca o
   * nome, então é resolvido aqui via `resolverAtores` antes de renderizar
   * (nome real como identificação principal, nunca o UUID — o UUID
   * continua existindo no registro de auditoria em si, só deixa de ser
   * usado como rótulo visual).
   */
  private async carregarAtividadesRecentes(): Promise<void> {
    if (!this._dashboardApi) return;
    try {
      const resultado = await this._dashboardApi.obterAtividadesRecentes();
      const body = this.root.querySelector('#secao-atividades');
      if (!body) return;
      if (resultado.status !== 'ok') {
        this.renderSecaoEstado('secao-atividades', 'Atividades recentes', resultado);
        return;
      }
      const atoresPorId = await this.resolverAtores(resultado.dado.map((registro) => registro.ator));
      body.innerHTML = `
        <div class="atividades-lista">
          ${resultado.dado
            .map((registro) => {
              const dataCompleta = formatarDataHora(registro.criadoEm);
              const ator = this.rotuloAtor(registro.ator, atoresPorId);
              return `
              <div class="atividade-item">
                <span class="atividade-ponto"></span>
                <div class="atividade-corpo">
                  <span class="atividade-quem">${escaparHtml(ator)}</span>
                  <span class="atividade-meta">
                    <span>${escaparHtml(rotuloAcaoAuditoria(registro.acao))}</span>
                    <span title="${escaparHtml(dataCompleta)}">${escaparHtml(tempoRelativo(registro.criadoEm))}</span>
                  </span>
                </div>
              </div>`;
            })
            .join('')}
        </div>`;
    } catch (erro) {
      logger.erro('falha ao carregar atividades recentes (dashboard CEO)', { erro });
      this.renderSecaoEstado('secao-atividades', 'Atividades recentes', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  /**
   * Resolve `ator` (id bruto de `RegistroAuditoria`) para um rótulo
   * exibível, tentando as 3 identidades reais que o barramento de
   * eventos usa como `ator` hoje (nunca uma 4ª inventada):
   * 1. `Usuario` (funcionário interno) — a maioria das ações
   *    administrativas (`usuario.autenticado`, `pedido.criado` via
   *    cadastro admin, etc.). Uma única listagem cobre todos de uma vez
   *    (`UsuariosApi.listar`, mesma fonte de `<erp-usuarios-page>`).
   * 2. `Parceiro` — login no Portal do Parceiro
   *    (`parceiro.portal_autenticado`, ator = `parceiroId`). Reaproveita
   *    a mesma listagem completa já usada pelo KPI "Parceiros ativos".
   * 3. `Cliente` — login no Portal do Cliente ou solicitação de suporte
   *    aberta pelo cliente (`cliente.portal_autenticado`/`ticket.criado`,
   *    ator = `clienteId`). Sem endpoint de listagem em massa por id no
   *    módulo Clientes — busca individual (`ClientesApi.buscarPorId`),
   *    só para os ids que sobraram sem nome depois dos passos 1 e 2
   *    (tipicamente 0-2 por carregamento, nunca uma por atividade).
   * Ids que não batem com nenhuma das 3 fontes (usuário/parceiro/cliente
   * excluído, id inconsistente) ficam de fora do mapa — `rotuloAtor`
   * decide o fallback.
   */
  private async resolverAtores(atores: string[]): Promise<Map<string, string>> {
    const mapa = new Map<string, string>();
    const idsUnicos = Array.from(new Set(atores.filter((id) => id !== 'system' && id !== 'scheduler')));
    if (idsUnicos.length === 0) return mapa;

    if (this._usuariosApi) {
      try {
        const resultado = await this._usuariosApi.listar();
        if (resultado.status === 'ok') {
          for (const usuario of resultado.dado) {
            if (idsUnicos.includes(usuario.id)) mapa.set(usuario.id, usuario.nome);
          }
        }
      } catch (erro) {
        logger.erro('falha ao resolver usuários para atividades recentes (dashboard CEO)', { erro });
      }
    }

    let restantes = idsUnicos.filter((id) => !mapa.has(id));
    if (restantes.length === 0) return mapa;

    if (this._parceirosApi) {
      try {
        const resultado = await this._parceirosApi.listar();
        if (resultado.status === 'ok') {
          for (const parceiro of resultado.dado) {
            if (restantes.includes(parceiro.id)) mapa.set(parceiro.id, parceiro.nome);
          }
        }
      } catch (erro) {
        logger.erro('falha ao resolver parceiros para atividades recentes (dashboard CEO)', { erro });
      }
    }

    restantes = idsUnicos.filter((id) => !mapa.has(id));
    if (restantes.length === 0 || !this._clientesApi) return mapa;

    await Promise.all(
      restantes.map(async (id) => {
        try {
          const cliente = await this._clientesApi!.buscarPorId(id);
          mapa.set(id, cliente.nome);
        } catch {
          // Não corresponde a usuário, parceiro nem cliente conhecido — sem entrada no mapa,
          // `rotuloAtor` decide o fallback (nunca um nome inventado).
        }
      })
    );

    return mapa;
  }

  /** `'system'`/`'scheduler'` viram rótulo fixo em português; qualquer outro id resolvido vira o nome real (usuário/parceiro/cliente); sem resolução, um rótulo genérico com o id encurtado — nunca o UUID inteiro como identificação principal, mas nunca escondido por completo (mesmo padrão de `pedido.id` truncado na tabela "Pedidos recentes"). */
  private rotuloAtor(ator: string, atoresPorId: Map<string, string>): string {
    if (ator === 'system') return 'O sistema';
    if (ator === 'scheduler') return 'O agendador';
    return atoresPorId.get(ator) ?? `Usuário ${ator.slice(0, 8)}…`;
  }

  private renderVazio(id: string, titulo: string, descricao: string): void {
    const body = this.root.querySelector(`#${id}`);
    if (!body) return;
    body.innerHTML = `<pg-empty-state variant="vazio" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }

  private renderSecaoEstado(id: string, titulo: string, resultado: Resultado<unknown>): void {
    const body = this.root.querySelector(`#${id}`);
    if (!body) return;
    const variant = resultado.status === 'sem-permissao' ? 'sem-permissao' : resultado.status === 'erro' ? 'erro' : 'indisponivel';
    body.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(legendaDoResultado(resultado))}"></pg-empty-state>`;
  }

  private definirKpi(id: string, valor: string, dica?: string): void {
    const card = this.root.querySelector(`#${id}`);
    if (!card) return;
    card.setAttribute('estado', 'ok');
    card.setAttribute('valor', valor);
    if (dica) card.setAttribute('dica', dica);
  }

  /** Só tendência/sparkline de um KPI já renderizado, sem tocar `estado`/`valor` — mesmo padrão de `definirSparkline` em `dashboard.page.ts` (evita corrida com quem define o valor do card, rodando em paralelo). */
  private definirTendencia(
    id: string,
    tendencia: string | undefined,
    tendenciaDirecao: 'alta' | 'baixa' | 'neutra' | undefined,
    pontos: number[] | undefined,
    corSparkline: string
  ): void {
    const card = this.root.querySelector(`#${id}`) as PgKpiCard | null;
    if (!card) return;
    if (tendencia) {
      card.setAttribute('tendencia', tendencia);
      card.setAttribute('tendencia-direcao', tendenciaDirecao ?? 'neutra');
    }
    if (pontos && pontos.length >= 2) {
      card.pontos = pontos;
      card.corSparkline = corSparkline;
    }
  }

  private aplicarEstadoKpi(id: string, resultado: Resultado<unknown>): void {
    const card = this.root.querySelector(`#${id}`);
    if (!card) return;
    card.setAttribute('estado', 'indisponivel');
    card.setAttribute('dica', legendaDoResultado(resultado));
  }

  /** Mesmo contrato de `definirKpi`, para os cards `<pg-donut-kpi>` da nova linha — `percentual` só quando existir uma proporção real (nunca inventada); sem ele, o anel do card desenha só a trilha. */
  private definirDonut(id: string, valor: string, opcoes?: { percentual?: number; dica?: string }): void {
    const card = this.root.querySelector(`#${id}`);
    if (!card) return;
    card.setAttribute('estado', 'ok');
    card.setAttribute('valor', valor);
    if (opcoes?.percentual !== undefined) card.setAttribute('percentual', String(opcoes.percentual));
    if (opcoes?.dica !== undefined) card.setAttribute('dica', opcoes.dica);
  }

  private aplicarEstadoDonut(id: string, motivo: string): void {
    const card = this.root.querySelector(`#${id}`);
    if (!card) return;
    card.setAttribute('estado', 'indisponivel');
    card.setAttribute('dica', motivo);
  }
}

/**
 * Projeta `dados` (só os meses com transação real, como a API devolve) sobre a janela EXATA dos
 * últimos `meses` meses, EM ORDEM CRONOLÓGICA, terminando no mês corrente (ex: `meses=6` em
 * agosto/2026 → mar, abr, mai, jun, jul, ago — nunca um mês fora dessa janela, nunca um mês
 * pulado no meio). Mês sem entrada correspondente em `dados` vira R$ 0,00 REAL (nunca inventado —
 * é exatamente o que "nenhuma transação naquele mês" significa), nunca omitido — usado só para
 * alimentar o gráfico "Evolução financeira" (câmbio isolado desta função: a tendência/sparkline
 * dos KPIs "Faturamento (mês)"/"Lucro do mês" continua usando os dados originais, sem este
 * preenchimento).
 */
function preencherMesesSemMovimentacao(dados: FaturamentoPeriodo[], meses: number, referencia: Date = new Date()): FaturamentoPeriodo[] {
  const porPeriodo = new Map(dados.map((p) => [p.periodo, p]));
  const resultado: FaturamentoPeriodo[] = [];
  for (let i = meses - 1; i >= 0; i--) {
    const data = new Date(referencia.getFullYear(), referencia.getMonth() - i, 1);
    const periodo = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
    resultado.push(
      porPeriodo.get(periodo) ?? { periodo, receitaCentavos: 0, despesaCentavos: 0, saldoCentavos: 0, lucroEstimadoCentavos: 0 }
    );
  }
  return resultado;
}

/** Mesmo formato de `dashboard.page.ts` — `"AAAA-MM"` → `"mmm aaaa"` (pt-BR). */
function formatarRotuloPeriodo(periodo: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(periodo);
  if (!m) return periodo;
  const [, ano, mes] = m;
  const data = new Date(Number(ano), Number(mes) - 1, 1);
  return data.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }).replace('.', '');
}

function primeiroNome(nomeCompleto: string): string {
  return nomeCompleto.trim().split(/\s+/)[0] ?? '';
}

function legendaDoResultado(resultado: Resultado<unknown>): string {
  switch (resultado.status) {
    case 'indisponivel':
      return resultado.motivo;
    case 'sem-permissao':
      return 'você não tem permissão para ver este dado';
    case 'erro':
      return resultado.mensagem;
    default:
      return 'não disponível';
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

/** Deriva tendência (% vs. período anterior) + pontos de sparkline de uma série real já buscada — nunca calcula nada quando a série é curta ou o denominador é zero (sem percentual sem sentido). Mesma fórmula de `dashboard.page.ts`, `calcularTendenciaEPontos`. */
function calcularTendenciaEPontos(serieCentavos: number[]): { tendencia?: string; tendenciaDirecao?: 'alta' | 'baixa' | 'neutra'; pontos?: number[] } {
  if (serieCentavos.length < 2) return {};
  const atual = serieCentavos[serieCentavos.length - 1]!;
  const anterior = serieCentavos[serieCentavos.length - 2]!;
  const pontos = serieCentavos;
  if (anterior === 0) return { pontos };
  const variacao = ((atual - anterior) / Math.abs(anterior)) * 100;
  const direcao: 'alta' | 'baixa' | 'neutra' = variacao > 0.5 ? 'alta' : variacao < -0.5 ? 'baixa' : 'neutra';
  return { tendencia: formatarPercentual(variacao), tendenciaDirecao: direcao, pontos };
}

function formatarPercentual(valor: number): string {
  const sinal = valor > 0 ? '+' : '';
  return `${sinal}${valor.toFixed(1).replace('.', ',')}%`;
}

const NOMES_MES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
] as const;

/** Mês/ano correntes do relógio local (ex: "Agosto 2026") — usado pelo card "Metas", cuja meta e contagem são sempre do mês atual, nunca de um período selecionável. */
function rotuloMesAtual(): string {
  const agora = new Date();
  return `${NOMES_MES[agora.getMonth()]} ${agora.getFullYear()}`;
}

/** Progresso de meta (0-1) → percentual sem casas decimais quando exato (ex: "0%", "100%"), com 1 casa quando fracionário (ex: "28,6%") — mesma convenção de vírgula decimal pt-BR do resto do dashboard, sem o sinal "+"/"-" de `formatarPercentual` (aqui não é uma variação, é uma fração de meta). */
function formatarPercentualMeta(fracao: number): string {
  const arredondado = Math.round(fracao * 1000) / 10;
  return Number.isInteger(arredondado) ? `${arredondado}%` : `${arredondado.toFixed(1).replace('.', ',')}%`;
}

/** "Falta 1 pedido" / "Faltam N pedidos" — reaproveitado pelo card "Metas" (barra de progresso) e pelo alerta de meta em texto no card "Alertas" (só chamado quando a meta ainda não foi atingida, `faltam` sempre ≥ 1 nesse caso). */
function rotuloFaltamPedidos(faltam: number): string {
  return faltam === 1 ? 'Falta 1 pedido' : `Faltam ${faltam} pedidos`;
}

/**
 * Próxima ocorrência de "dia X do mês" a partir de `hoje` — mesma regra pro aluguel recorrente
 * e pra parcela de financiamento (ambos vencem num dia fixo todo mês): se o dia já passou (ou é
 * hoje), a ocorrência é neste mês; senão, é no mês seguinte. `new Date(ano, mes+1, dia)` rola
 * o ano automaticamente quando `mes+1` estoura dezembro — sem cálculo manual de virada de ano.
 * Sempre calculado a partir de `hoje` de verdade (parâmetro com default `new Date()`), nunca um
 * mês fixo.
 */
function proximoVencimento(diaVencimento: number, hoje: Date = new Date()): Date {
  const ano = hoje.getFullYear();
  const mes = hoje.getMonth();
  if (hoje.getDate() <= diaVencimento) return new Date(ano, mes, diaVencimento);
  return new Date(ano, mes + 1, diaVencimento);
}

/** "Recorrente" (sem parcela definida, ex: aluguel) ou "Parcela N/T" (financiamento com fim definido). */
function rotuloStatusDespesa(despesa: DespesaFixa): string {
  return despesa.parcela ? `Parcela ${despesa.parcela.numero}/${despesa.parcela.total}` : 'Recorrente';
}

/** "DD/MM/AAAA" — usado só para financiamentos com parcela (ex: "PC Igor Roberto"), que precisam do mês/ano do vencimento, não só do dia (diferente do recorrente indefinido, que já deixa claro "todo dia X"). */
function formatarDataVencimento(data: Date): string {
  const dia = String(data.getDate()).padStart(2, '0');
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  return `${dia}/${mes}/${data.getFullYear()}`;
}

/**
 * Todas as despesas fixas (`DESPESAS_FIXAS`) com sua PRÓXIMA ocorrência de vencimento a partir
 * de `hoje`, ordenadas cronologicamente — fonte única reaproveitada pelo card "Alertas" (resumo
 * textual), "Próximo vencimento" (destaque de 1 item) e "Próximos pagamentos" (lista completa),
 * nenhuma lógica duplicada entre os três.
 */
function proximasDespesasOrdenadas(hoje: Date = new Date()): Array<{ despesa: DespesaFixa; vencimento: Date }> {
  return DESPESAS_FIXAS.map((despesa) => ({ despesa, vencimento: proximoVencimento(despesa.diaVencimento, hoje) })).sort(
    (a, b) => a.vencimento.getTime() - b.vencimento.getTime()
  );
}

/** Dias corridos entre hoje (hora zerada, comparação por dia de calendário, não por 24h exatas) e `vencimento` (já à meia-noite — ver `proximoVencimento`). Nunca negativo em uso normal: `proximoVencimento` sempre devolve hoje ou uma data futura. */
function diasAteVencimento(vencimento: Date, hoje: Date = new Date()): number {
  const hojeSemHora = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  return Math.round((vencimento.getTime() - hojeSemHora.getTime()) / 86_400_000);
}

/** "Vence hoje" / "Vence amanhã" / "Vence em N dias" — nunca "vence em 0 dias" nem "vence em 1 dias". */
function rotuloDiasRestantes(dias: number): string {
  if (dias <= 0) return 'Vence hoje';
  if (dias === 1) return 'Vence amanhã';
  return `Vence em ${dias} dias`;
}

/**
 * "Próximo vencimento" — só a despesa fixa de vencimento mais próximo (`proximasDespesasOrdenadas`,
 * a MESMA fonte do alerta de vencimento em "Alertas" e da lista completa em "Próximos
 * pagamentos" — nenhuma consulta ou cálculo duplicado). Selo circular com dia+mês em destaque
 * (identidade PG, dourado) + nome, parcela quando houver, valor e "vence em N dias". Retorna o
 * HTML pronto pra embutir direto no template de `render()` — dado 100% local/síncrono, sem
 * estado de carregando/erro (não depende de rede). Lista vazia (`DESPESAS_FIXAS = []`, cenário
 * futuro) cai num estado vazio coerente, nunca "indisponível".
 */
function htmlProximoVencimento(): string {
  const hoje = new Date();
  const proximas = proximasDespesasOrdenadas(hoje);
  if (proximas.length === 0) {
    return `<pg-empty-state variant="vazio" titulo="Próximo vencimento" descricao="nenhuma despesa recorrente cadastrada"></pg-empty-state>`;
  }
  const { despesa, vencimento } = proximas[0]!;
  const mesAbrev = NOMES_MES[vencimento.getMonth()]!.slice(0, 3).toUpperCase();
  const linhaParcela = despesa.parcela
    ? `<span class="vencimento-parcela">${escaparHtml(rotuloStatusDespesa(despesa))}</span>`
    : '';
  return `
    <div class="vencimento-card">
      <div class="vencimento-selo" title="${escaparHtml(formatarDataVencimento(vencimento))}">
        <span class="dia">${vencimento.getDate()}</span>
        <span class="mes">${mesAbrev}</span>
      </div>
      <div class="vencimento-info">
        <span class="vencimento-nome">${escaparHtml(despesa.nome)}</span>
        ${linhaParcela}
        <span class="vencimento-valor">${escaparHtml(formatBRL(centavosParaReais(despesa.valorCentavos)))}</span>
        <span class="vencimento-meta">${escaparHtml(rotuloDiasRestantes(diasAteVencimento(vencimento, hoje)))}</span>
      </div>
    </div>`;
}

/** Parcelas restantes A PARTIR da próxima pendente (inclusive) — ex: próxima é 3/12 → restam 10 (3,4,...,12); próxima 12/12 → resta 1 (só ela mesma). Nunca conta as já pagas (numero-1) como restantes. */
function rotuloParcelasRestantes(parcela: { numero: number; total: number }): string {
  const restantes = parcela.total - parcela.numero + 1;
  return restantes === 1 ? 'Resta 1 parcela' : `Restam ${restantes} parcelas`;
}

/**
 * "Próximos pagamentos" (linha de destaque, substitui "Seus bônus PG") — TODAS as despesas
 * fixas (`DESPESAS_FIXAS`, via `proximasDespesasOrdenadas`, a MESMA fonte do card "Alertas" e
 * "Próximo vencimento" — nenhuma lógica duplicada), em lista, ordenadas cronologicamente. Sem
 * estado de carregando/erro — dado 100% local/síncrono. Lista vazia (`DESPESAS_FIXAS = []`,
 * cenário futuro) cai num estado vazio coerente, nunca "indisponível".
 */
function htmlProximosPagamentosLista(): string {
  const itens = proximasDespesasOrdenadas();

  if (itens.length === 0) {
    return `<pg-empty-state variant="vazio" titulo="Próximos pagamentos" descricao="nenhuma despesa recorrente cadastrada"></pg-empty-state>`;
  }

  return `
    <div class="pagamentos-lista">
      ${itens
        .map(({ despesa, vencimento }) => {
          // Financiamento com parcela: mês/ano completo do vencimento (não só o dia) + quantas
          // parcelas restam a partir da próxima pendente, inclusive. Recorrente indefinido (sem
          // fim, ex: aluguel) continua exatamente como antes — "Vence dia X", sem 2ª linha.
          const metaPrincipal = despesa.parcela
            ? `${rotuloStatusDespesa(despesa)} · Vence em ${formatarDataVencimento(vencimento)}`
            : `${rotuloStatusDespesa(despesa)} · Vence dia ${vencimento.getDate()}`;
          const linhaRestantes = despesa.parcela
            ? `<span class="pagamento-meta">${escaparHtml(rotuloParcelasRestantes(despesa.parcela))}</span>`
            : '';
          return `
        <div class="pagamento-item">
          <div class="pagamento-linha">
            <span class="pagamento-nome">${escaparHtml(despesa.nome)}</span>
            <span class="pagamento-valor">${escaparHtml(formatBRL(centavosParaReais(despesa.valorCentavos)))}</span>
          </div>
          <span class="pagamento-meta">${escaparHtml(metaPrincipal)}</span>
          ${linhaRestantes}
        </div>`;
        })
        .join('')}
    </div>`;
}

/** Mesmo dia de calendário local (não UTC) — "hoje"/"ontem" do KPI "Pedidos novos" precisa bater com o dia que o próprio usuário está vendo no relógio. */
function mesmoDiaLocal(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Contagem por `clienteId` sobre uma lista de pedidos já buscada — usada só para os poucos clientes recém-cadastrados exibidos em "Últimos clientes" (nenhuma chamada nova por cliente). */
function contarPedidosPorCliente(pedidos: Pedido[]): Map<string, number> {
  const mapa = new Map<string, number>();
  for (const pedido of pedidos) mapa.set(pedido.clienteId, (mapa.get(pedido.clienteId) ?? 0) + 1);
  return mapa;
}

function rotuloContagemPedidos(quantidade: number): string {
  return quantidade === 1 ? '1 pedido' : `${quantidade} pedidos`;
}

/**
 * Coluna "Produto" — nome real do produto é a identificação principal
 * (`descricao` de `ItemPedido`, nunca inventado); o id do pedido some
 * como sufixo secundário abreviado, mesmo padrão de abreviação já usado
 * aqui antes (`id.slice(0, 8)`). Sem os itens resolvidos (falha
 * individual best-effort de `resolverProdutosDosPedidos`, ou pedido sem
 * item sincronizado), cai no id abreviado sozinho — nunca um nome de
 * produto inventado só para preencher a célula.
 */
function pedidoParaLinhaOperacional(pedido: Pedido, produto: string | undefined): Record<string, string | PgTableBadgeCell> {
  const idAbreviado = pedido.id.length > 8 ? `${pedido.id.slice(0, 8)}…` : pedido.id;
  return {
    pedido: produto ? `${produto} · #${idAbreviado}` : `Pedido #${idAbreviado}`,
    status: DESCRICAO_STATUS_PEDIDO[pedido.status],
    pagamento: { texto: rotuloStatusPedido(pedido.status), tom: TOM_STATUS_PEDIDO[pedido.status] },
    valor: formatBRL(centavosParaReais(pedido.valorTotalCentavos)),
    criado: formatarDataHora(pedido.criadoEm),
  };
}

/** Um pedido pode ter mais de um item (`PedidoComItens.itens`) — junta as descrições reais em vez de assumir um único produto; quantidade > 1 vira sufixo "2x" (não uma 2ª linha, `pg-table` só renderiza texto puro). */
function formatarProdutosPedido(itens: PedidoComItens['itens']): string {
  return itens.map((item) => (item.quantidade > 1 ? `${item.descricao} (${item.quantidade}x)` : item.descricao)).join(' + ');
}

function rotuloAcaoAuditoria(acao: string): string {
  return ROTULOS_ACAO_AUDITORIA[acao] ?? acao.replace(/[._]/g, ' ');
}

/** "agora" / "há N min" / "há N h" / "há N dia(s)" — mesma ideia de `timeAgo` do painel antigo (`pgcustom-art/admin/dashboard.html`), adaptada ao pt-BR completo pedido nesta sessão. */
function tempoRelativo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutos = Math.floor(diffMs / 60000);
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  const dias = Math.floor(horas / 24);
  return `há ${dias} dia${dias > 1 ? 's' : ''}`;
}

/** Mesmo path do ícone "erro" de `pg-empty-state` (triângulo de exclamação) — reaproveitado aqui em vez de desenhar um novo, só trocando a cor pelo tom do alerta. */
function svgAlertaIcone(tom: 'alerta' | 'erro'): string {
  const cor = tom === 'erro' ? 'var(--pg-color-erro, #e5484d)' : 'var(--pg-color-alerta, #e0a52c)';
  return `<svg class="alerta-icone" viewBox="0 0 24 24" fill="none" stroke="${cor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>`;
}

customElements.define('erp-dashboard-ceo-page', ErpDashboardCeoPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-dashboard-ceo-page': ErpDashboardCeoPage;
  }
}
